import { call, put, select, takeLatest } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { axiosInstance } from 'setup/axiosInstance';
import { adminActions, AdminFilters } from 'features/feedback/store/admin.slice';
import { AdminFeedbackTO, Page } from 'features/feedback/shared/feedback.types';

export function* checkAccessSaga() {
  try {
    // Probe admin access. Accept both 403 and 401 as valid (non-error) statuses so axios does NOT
    // reject on them: a rejected probe would hit the shared response interceptor and could log the
    // user out. Only 200 means "admin"; any denial (403, or 401 if the backend answers that for an
    // authorization failure) means "not admin". 200 = admin, otherwise = not admin.
    const resp: AxiosResponse = yield call(axiosInstance.get, '/admin/feedback', {
      params: { size: 1 },
      validateStatus: (s: number) => s === 200 || s === 403 || s === 401,
    });
    yield put(adminActions.setAdmin(resp.status === 200));
  } catch {
    yield put(adminActions.setAdmin(false));
  }
}

export function* fetchAdminSaga(): Generator<unknown, void, any> {
  try {
    const filters: AdminFilters = yield select((s: any) => s.admin.filters);
    const params: Record<string, unknown> = { page: 0, size: 50 };
    if (filters.type !== 'ALL') params.type = filters.type;
    if (filters.status !== 'ALL') params.status = filters.status;
    if (filters.area !== 'ALL') params.area = filters.area;
    const resp: AxiosResponse<Page<AdminFeedbackTO>> = yield call(axiosInstance.get, '/admin/feedback', { params });
    yield put(adminActions.adminSet(resp.data));
  } catch (e) {
    console.error(e);
    yield put(adminActions.adminError());
  }
}

export function* patchSaga(action: ReturnType<typeof adminActions.patch>) {
  try {
    const { id, body } = action.payload;
    const resp: AxiosResponse<AdminFeedbackTO> = yield call(axiosInstance.patch, `/admin/feedback/${id}`, body);
    yield put(adminActions.patchSuccess(resp.data));
  } catch (e) {
    console.error(e);
    yield put(adminActions.patchError());
  }
}

export default function* adminSaga() {
  yield takeLatest(adminActions.checkAccess, checkAccessSaga);
  yield takeLatest(adminActions.adminGet, fetchAdminSaga);
  yield takeLatest(adminActions.patch, patchSaga);
}
